import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Text,
  Heading,
  HStack,
  IconButton,
  Badge,
  Flex,
  VStack,
  useBreakpointValue,
} from "@chakra-ui/react";
import { FaBus, FaHeart, FaPen } from "react-icons/fa";
import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import Translate from "../../components/LanguageTranslation/Translate";
import { useCurrency } from "../CurrencyExchange/CurrencyContext";

const OtherCard = ({ otherTickets = [] }) => {
  // console.log("busTicket", busTickets);
  const [current, setCurrent] = useState(0);
  const numVisibleCards = useBreakpointValue({ base: 1, md: 2, lg: 2 });
  // const numTotalCards = Array.isArray(busTicket) ? busTicket.length : 0;
  const numTotalCards = otherTickets?.length;
  const containerRef = useRef(null);
  const touchStartXRef = useRef(null);

  const [showCallout, setShowCallout] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowCallout(false), 9000);
    return () => clearTimeout(timer);
  }, []); // run once on mount

  const next = () => {
    setCurrent((prev) => (prev + numVisibleCards) % numTotalCards);
  };

  const prev = () => {
    setCurrent(
      (prev) => (prev - numVisibleCards + numTotalCards) % numTotalCards
    );
  };

  const handleTouchStart = (e) => {
    touchStartXRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartXRef.current - touchEndX;
    if (diff > 50) next();
    else if (diff < -50) prev();
  };

  const hasPrev = current > 0;
  const hasNext = current + numVisibleCards < numTotalCards;

  const { convertCurrency, currency } = useCurrency();

  if (!otherTickets || otherTickets.length === 0) return null;

  // Helper to format date
  const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

  return (
    <Flex
      position="relative"
      alignItems="center"
      justifyContent="center"
      padding={0}
      mt={3}
      width={{ base: "100%", md: "80%", lg: "100%" }} // Responsive width
    >
      {hasPrev && (
        <IconButton
          icon={<ArrowBackIcon />}
          isRound={true}
          size="md"
          onClick={prev}
          position="absolute"
          left={{ base: "1px", md: "10px" }}
          backgroundColor="white"
          boxShadow="base"
          zIndex="overlay"
        />
      )}
      <HStack
        ref={containerRef}
        overflow="hidden"
        width="full"
        h="auto"
        spacing={3}
        justifyContent={{ base: "center", md: "space-between" }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {otherTickets
          .slice(current, current + numVisibleCards)
          .map((otherTicket, index) => {
            // Use new field names
            const priceAmount = otherTicket.price ?? 0;
            const priceCurrency = otherTicket.currency ?? "USD";
            const convertedPrice = convertCurrency(priceAmount, priceCurrency);

            return (
              <Box
                key={index}
                bg="blackAlpha.50"
                padding={2}
                borderRadius="xl"
                boxShadow="md"
                width={{ base: "80%", md: "50%", lg: "50%" }}
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
              >
                <VStack
                  p={2}
                  borderRadius="xl"
                  alignItems="flex-start"
                  w="100%"
                  spacing={3}
                  bg="white"
                  h="100%"
                  position="relative"
                >
                  {/* Price and Company Info */}
                  <Box width="100%" position="relative">
                    <HStack spacing={1}>
                      <FaBus size={40} style={{ marginRight: 16 }} />
                      <Heading size="sm" mb={0} mr={2} noOfLines={1}>
                        <Translate
                          text={otherTicket.company || "Other Company"}
                        />
                      </Heading>
                    </HStack>

                    <Badge
                      bg="none"
                      color="#FFC719"
                      border="1px solid"
                      borderColor="#FFC719"
                      borderRadius="md"
                      textTransform="capitalize"
                      fontSize="xs"
                      fontWeight="bold"
                      mb={2}
                      mt={2}
                    >
                      <Translate text="Other Transportation" />
                    </Badge>
                    <Text fontWeight="bold" fontSize="md" mt={2} mb={2}>
                      <Translate text="Live price:" /> {currency}{" "}
                      {convertedPrice}
                    </Text>
                  </Box>
                  <HStack
                    justify="space-between"
                    width="full"
                    spacing={0}
                    bg="blackAlpha.50"
                    padding={2}
                    borderRadius="xl"
                  >
                    <Flex
                      justify="space-between"
                      mt={2}
                      mb={2}
                      width="100%"
                      align="center"
                    >
                      <Text fontSize="sm" mb={0}>
                        {otherTicket.start_location}
                      </Text>
                      <Text fontSize="lg" mx={2} mb={0}>
                        ➔
                      </Text>
                      <Text fontSize="sm" mb={0}>
                        {otherTicket.end_location}
                      </Text>
                    </Flex>
                  </HStack>
                  <Flex justify="space-between" mb={2} width="100%">
                    <Text fontSize="sm">
                      <Translate text="Departure:" />{" "}
                      {formatDate(otherTicket.start_date)}
                    </Text>
                    <Text fontSize="sm">
                      <Translate text="Arrival:" />{" "}
                      {formatDate(otherTicket.end_date)}
                    </Text>
                  </Flex>
                </VStack>
              </Box>
            );
          })}
      </HStack>
      {hasNext && (
        <IconButton
          icon={<ArrowForwardIcon />}
          isRound={true}
          size="md"
          onClick={next}
          position="absolute"
          right={{ base: "1px", md: "10px" }}
          backgroundColor="white"
          boxShadow="base"
          zIndex="overlay"
        />
      )}
    </Flex>
  );
};

export default OtherCard;
